import { useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  BarChart3,
  CalendarRange,
  ChevronLeft,
  CirclePlus,
  FolderKanban,
  ListTodo,
  Settings,
  Target,
  TimerReset,
  WifiOff
} from 'lucide-react';
import { useAppStore } from '@/app/store';
import { QuickTaskDialog } from '@/features/tasks/quick-task-dialog';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { useScrollToTop } from '@/hooks/use-scroll-to-top';
import { cn } from '@/lib/cn';
import { AppLogo } from './app-logo';

const navItems = [
  { label: 'Heute', href: '/today', icon: ListTodo },
  { label: 'Plan', href: '/plan', icon: CalendarRange },
  { label: 'Projekte', href: '/projects', icon: FolderKanban },
  { label: 'Statistik', href: '/statistics', icon: BarChart3 },
  { label: 'Einstellungen', href: '/settings', icon: Settings }
] as const;

function SidebarLink({
  href,
  icon: Icon,
  label,
  compact
}: (typeof navItems)[number] & { compact: boolean }) {
  return (
    <NavLink
      aria-label={label}
      className={({ isActive }) =>
        cn(
          'group flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition-colors',
          compact && 'justify-center px-0',
          isActive ? 'bg-accent/12 text-accent' : 'text-muted hover:bg-elevated hover:text-ink'
        )
      }
      to={href}
    >
      <Icon aria-hidden="true" className="size-[1.15rem] shrink-0" />
      {!compact ? <span>{label}</span> : null}
    </NavLink>
  );
}

export function AppShell() {
  const collapsed = useAppStore((state) => state.sidebarCollapsed);
  const setCollapsed = useAppStore((state) => state.setSidebarCollapsed);
  const setCommandOpen = useAppStore((state) => state.setCommandOpen);
  const online = useOnlineStatus();
  useKeyboardShortcuts();
  useScrollToTop();

  useEffect(() => {
    document.body.dataset.shell = 'ready';
    return () => {
      delete document.body.dataset.shell;
    };
  }, []);

  return (
    <div className="min-h-dvh bg-canvas text-ink">
      <aside
        className={cn(
          'safe-top fixed inset-y-0 left-0 z-40 hidden border-r border-line bg-surface/95 px-3 pb-4 backdrop-blur-xl transition-[width] lg:flex lg:flex-col',
          collapsed ? 'w-[5.25rem]' : 'w-64'
        )}
      >
        <div className={cn('flex h-20 items-center px-2', collapsed && 'justify-center px-0')}>
          <AppLogo compact={collapsed} />
        </div>
        <button
          aria-label={collapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'}
          className="absolute -right-3 top-8 grid size-7 place-items-center rounded-full border border-line bg-surface text-muted shadow-soft hover:text-ink"
          onClick={() => setCollapsed(!collapsed)}
          type="button"
        >
          <ChevronLeft
            aria-hidden="true"
            className={cn('size-3.5 transition-transform', collapsed && 'rotate-180')}
          />
        </button>

        <button
          className={cn(
            'mb-5 flex min-h-11 items-center justify-center gap-2 rounded-xl bg-accent px-3 text-sm font-bold text-accent-ink shadow-soft hover:bg-accent/90',
            collapsed && 'px-0'
          )}
          onClick={() => setCommandOpen(true)}
          type="button"
        >
          <CirclePlus aria-hidden="true" className="size-[1.15rem]" />
          {!collapsed ? (
            <>
              <span>Neue Aufgabe</span>
              <kbd className="ml-auto rounded-md bg-black/10 px-1.5 py-0.5 text-[10px]">N</kbd>
            </>
          ) : null}
        </button>

        <nav aria-label="Hauptnavigation" className="space-y-1">
          {navItems.map((item) => (
            <SidebarLink compact={collapsed} key={item.href} {...item} />
          ))}
        </nav>

        <div className="mt-auto space-y-3">
          <NavLink
            aria-label="Fokus starten"
            className={({ isActive }) =>
              cn(
                'flex min-h-12 items-center gap-3 rounded-xl border border-line bg-elevated/75 px-3 text-sm font-semibold hover:border-accent/40',
                collapsed && 'justify-center px-0',
                isActive && 'border-accent/40 text-accent'
              )
            }
            to="/focus"
          >
            <TimerReset aria-hidden="true" className="size-5 text-accent" />
            {!collapsed ? (
              <div>
                <p>Fokus</p>
                <p className="text-[11px] font-normal text-muted">Taste F</p>
              </div>
            ) : null}
          </NavLink>
          {!collapsed ? (
            <div className="rounded-xl border border-line bg-canvas p-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted">
                <Target aria-hidden="true" className="size-3.5 text-accent" /> Heute zählt
              </div>
              <p className="mt-2 text-xs leading-relaxed text-muted">
                Ein sauberer nächster Schritt ist genug.
              </p>
            </div>
          ) : null}
        </div>
      </aside>

      <div
        className={cn('min-h-dvh transition-[padding]', collapsed ? 'lg:pl-[5.25rem]' : 'lg:pl-64')}
      >
        <div className="safe-top sticky top-0 z-30 border-b border-line bg-canvas/85 backdrop-blur-xl lg:hidden">
          <div className="flex h-16 items-center justify-between px-4">
            <AppLogo />
            {!online ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning">
                <WifiOff aria-hidden="true" className="size-3.5" /> Offline
              </span>
            ) : null}
          </div>
        </div>

        <main className="mx-auto w-full max-w-[92rem] px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-6 sm:px-6 lg:px-8 lg:pb-10 lg:pt-9">
          <Outlet />
        </main>
      </div>

      <button
        aria-label="Neue Aufgabe"
        className="fixed bottom-[calc(5.6rem+env(safe-area-inset-bottom))] right-4 z-40 grid size-14 place-items-center rounded-2xl bg-accent text-accent-ink shadow-lift hover:bg-accent/90 lg:hidden"
        onClick={() => setCommandOpen(true)}
        type="button"
      >
        <CirclePlus aria-hidden="true" className="size-6" />
      </button>

      <nav
        aria-label="Mobile Hauptnavigation"
        className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/95 px-1 backdrop-blur-xl lg:hidden"
      >
        <div className="mx-auto grid h-[4.6rem] max-w-xl grid-cols-5">
          {navItems.map(({ href, icon: Icon, label }) => (
            <NavLink
              aria-label={label}
              className={({ isActive }) =>
                cn(
                  'flex min-w-0 flex-col items-center justify-center gap-1 text-[10px] font-semibold text-muted',
                  isActive && 'text-accent'
                )
              }
              key={href}
              to={href}
            >
              <Icon aria-hidden="true" className="size-5" />
              <span className="max-w-full truncate">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      <QuickTaskDialog />
    </div>
  );
}
